import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Card, Select, Row, Col, Statistic, Empty, Spin, Button, Tag } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faChartBar } from '@fortawesome/free-solid-svg-icons';
import { fetchCampaigns, compareCampaigns } from '../../../Slices/bulkOutreachSlice';
import { useNavigate } from 'react-router-dom';

const STATUS_COLORS = { draft: '#8b5cf6', active: '#10b981', paused: '#f59e0b', completed: '#3b82f6', cancelled: '#ef4444' };
const METRICS = ['sent', 'delivered', 'opened', 'clicked', 'replied', 'bounced'];
const METRIC_COLORS = ['#3b82f6', '#10b981', '#8b5cf6', '#6366f1', '#14b8a6', '#ef4444'];

const glassCard = {
  borderRadius: 16, border: '1px solid rgba(255,255,255,0.18)',
  background: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(248,250,252,0.9) 100%)',
  boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
};

const CampaignComparison = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { campaigns, comparison, loading } = useSelector(state => state.bulkOutreach);
  const [selectedIds, setSelectedIds] = useState([]);

  useEffect(() => { dispatch(fetchCampaigns({ limit: 100 })); }, [dispatch]);

  const handleCompare = () => {
    if (selectedIds.length >= 2) dispatch(compareCampaigns(selectedIds));
  };

  const maxVal = (metric) => Math.max(...comparison.map(c => c.metrics?.[metric] || 0), 1);

  return (
    <div style={{ padding: '24px 32px', minHeight: '100vh', background: '#f8fafc' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 }}>
        <Button type="text" onClick={() => navigate('/bulk-outreach')} icon={<FontAwesomeIcon icon={faArrowLeft} />} />
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#1a1a2e' }}>
          <FontAwesomeIcon icon={faChartBar} style={{ marginRight: 10, color: '#8b5cf6' }} />
          Campaign Comparison
        </h2>
      </div>

      <Card style={{ ...glassCard, marginBottom: 24, padding: '8px 0' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '0 16px' }}>
          <Select
            mode="multiple"
            placeholder="Select 2-4 campaigns to compare"
            value={selectedIds}
            onChange={setSelectedIds}
            style={{ flex: 1 }}
            maxTagCount={4}
            options={(campaigns || []).map(c => ({ value: c._id, label: `${c.name} (${c.status})` }))}
            filterOption={(input, opt) => opt.label.toLowerCase().includes(input.toLowerCase())}
          />
          <Button type="primary" disabled={selectedIds.length < 2} onClick={handleCompare}
            style={{ background: '#8b5cf6', borderColor: '#8b5cf6', borderRadius: 8 }}>
            Compare
          </Button>
        </div>
      </Card>

      {loading && <div style={{ textAlign: 'center', padding: 60 }}><Spin size="large" /></div>}

      {!loading && comparison.length === 0 && (
        <Empty description="Select 2 or more campaigns and click Compare" style={{ marginTop: 60 }} />
      )}

      {comparison.length >= 2 && (
        <>
          {/* Campaign headers */}
          <Row gutter={16} style={{ marginBottom: 24 }}>
            {comparison.map((c, i) => (
              <Col key={c.id} span={Math.floor(24 / comparison.length)}>
                <Card style={{ ...glassCard, textAlign: 'center' }}>
                  <h3 style={{ margin: '0 0 8px', fontSize: 16, fontWeight: 600, color: '#1a1a2e' }}>{c.name}</h3>
                  <Tag color={STATUS_COLORS[c.status]}>{c.status}</Tag>
                  <div style={{ marginTop: 8, fontSize: 12, color: '#6b7280' }}>
                    {c.startedAt ? new Date(c.startedAt).toLocaleDateString() : 'Not started'}
                  </div>
                </Card>
              </Col>
            ))}
          </Row>

          {/* Metric comparison bars */}
          {METRICS.map((metric, mi) => (
            <Card key={metric} style={{ ...glassCard, marginBottom: 12 }} bodyStyle={{ padding: '12px 20px' }}>
              <div style={{ fontWeight: 600, fontSize: 13, color: '#6b7280', textTransform: 'uppercase', marginBottom: 8 }}>{metric}</div>
              <Row gutter={16}>
                {comparison.map((c) => {
                  const val = c.metrics?.[metric] || 0;
                  const pct = (val / maxVal(metric)) * 100;
                  return (
                    <Col key={c.id} span={Math.floor(24 / comparison.length)}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div style={{ flex: 1, background: '#f1f5f9', borderRadius: 6, height: 28, overflow: 'hidden' }}>
                          <div style={{
                            width: `${pct}%`, height: '100%', borderRadius: 6,
                            background: `linear-gradient(90deg, ${METRIC_COLORS[mi]}cc, ${METRIC_COLORS[mi]})`,
                            transition: 'width 0.5s ease',
                          }} />
                        </div>
                        <span style={{ fontWeight: 700, fontSize: 16, color: '#1a1a2e', minWidth: 50, textAlign: 'right' }}>{val}</span>
                      </div>
                    </Col>
                  );
                })}
              </Row>
            </Card>
          ))}

          {/* Rate comparison */}
          <Card style={{ ...glassCard, marginTop: 24 }} bodyStyle={{ padding: 20 }}>
            <h3 style={{ margin: '0 0 16px', fontWeight: 600, color: '#1a1a2e' }}>Rates (%)</h3>
            <Row gutter={16}>
              {comparison.map((c) => (
                <Col key={c.id} span={Math.floor(24 / comparison.length)}>
                  <div style={{ textAlign: 'center' }}>
                    <Statistic title="Open Rate" value={c.metrics?.openRate || 0} suffix="%" valueStyle={{ color: '#8b5cf6', fontWeight: 700 }} />
                    <Statistic title="Click Rate" value={c.metrics?.clickRate || 0} suffix="%" valueStyle={{ color: '#3b82f6', fontWeight: 700 }} style={{ marginTop: 12 }} />
                    <Statistic title="Reply Rate" value={c.metrics?.replyRate || 0} suffix="%" valueStyle={{ color: '#10b981', fontWeight: 700 }} style={{ marginTop: 12 }} />
                    <Statistic title="Bounce Rate" value={c.metrics?.bounceRate || 0} suffix="%" valueStyle={{ color: '#ef4444', fontWeight: 700 }} style={{ marginTop: 12 }} />
                  </div>
                </Col>
              ))}
            </Row>
          </Card>

          {/* Per-step comparison */}
          {comparison[0]?.perStep?.length > 0 && (
            <Card style={{ ...glassCard, marginTop: 24 }} bodyStyle={{ padding: 20 }}>
              <h3 style={{ margin: '0 0 16px', fontWeight: 600, color: '#1a1a2e' }}>Per-Step Breakdown</h3>
              {comparison[0].perStep.map((_, si) => (
                <div key={si} style={{ marginBottom: 16 }}>
                  <div style={{ fontWeight: 600, fontSize: 13, color: '#8b5cf6', marginBottom: 8 }}>Step {si + 1}</div>
                  <Row gutter={16}>
                    {comparison.map((c) => {
                      const step = c.perStep?.[si];
                      if (!step) return <Col key={c.id} span={Math.floor(24 / comparison.length)}><span style={{ color: '#9ca3af' }}>N/A</span></Col>;
                      return (
                        <Col key={c.id} span={Math.floor(24 / comparison.length)}>
                          <div style={{ display: 'flex', gap: 16, fontSize: 13 }}>
                            <span>Sent: <b>{step.metrics?.sent || 0}</b></span>
                            <span>Opened: <b>{step.metrics?.opened || 0}</b></span>
                            <span>Replied: <b>{step.metrics?.replied || 0}</b></span>
                          </div>
                        </Col>
                      );
                    })}
                  </Row>
                </div>
              ))}
            </Card>
          )}
        </>
      )}
    </div>
  );
};

export default CampaignComparison;
